import React from 'react';
import {
    Dumbbell,
    Calendar,
    TrendingUp,
    Users,
    UserCog,
    LogOut,
    Trophy,
    UserCheck,
    Activity,
    HelpCircle,
    Calculator
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../ui/LanguageSwitcher';
import { useNotifications } from '../../hooks/useNotifications';
import { useRequestCount } from '../../hooks/useRequestCount';

const Sidebar = ({
    user,
    profile,
    role,
    activeTab,
    onNavigate,
    onLogout,
    isProfileComplete,
    onShowTutorial
}) => {
    const { t } = useTranslation();
    const { pendingRequests } = useNotifications(user?.uid);
    const friendRequestsCount = useRequestCount(user?.uid);

    const navItems = [
        {
            id: 'home',
            label: role === 'trainer' ? t('sidebar.trainer') : t('sidebar.routines'),
            icon: role === 'trainer' ? Users : Calendar,
            disabled: !isProfileComplete,
            elementId: 'nav-home'
        },
        {
            id: 'analytics',
            label: t('sidebar.analytics'),
            icon: TrendingUp,
            disabled: !isProfileComplete,
            elementId: 'nav-analytics',
            hidden: role === 'trainer'
        },
        {
            id: 'tools',
            label: t('sidebar.tools'),
            icon: Calculator,
            disabled: false,
            elementId: 'nav-tools'
        },
        {
            id: 'feed',
            label: t('social.feed_tab'),
            icon: Activity,
            disabled: !isProfileComplete,
            elementId: 'nav-feed'
        },
        {
            id: 'ranking',
            label: t('social.ranking_tab'),
            icon: Trophy,
            disabled: !isProfileComplete,
            elementId: 'nav-ranking'
        },
        {
            id: 'friends',
            label: t('sidebar.friends'),
            icon: Users,
            disabled: !isProfileComplete,
            elementId: 'nav-friends'
        },
        // Coach tab only for athletes
        ...(role === 'athlete' ? [{
            id: 'coach',
            label: t('sidebar.coach'),
            icon: UserCheck,
            disabled: !isProfileComplete,
            elementId: 'nav-coach'
        }] : []),
        {
            id: 'profile',
            label: t('sidebar.profile'),
            icon: UserCog,
            disabled: false,
            elementId: 'nav-profile'
        }
    ];

    return (
        <aside className="hidden md:flex flex-col w-64 h-screen fixed left-0 top-0 bg-black/95 backdrop-blur-xl border-r border-neutral-900 z-50">
            {/* Header / Logo */}
            <div className="p-6 border-b border-neutral-900">
                <div className="flex items-center gap-3">
                    <div className="bg-red-600 p-1.5 rounded-lg shadow-[0_0_15px_rgba(220,38,38,0.5)] -rotate-12 overflow-hidden transform-gpu border border-red-600">
                        <Dumbbell size={20} className="text-white rotate-45" strokeWidth={2.5} />
                    </div>
                    <div className="flex flex-col">
                        <span className="font-black text-white tracking-tighter text-xl italic leading-none">
                            GYM TRACKER
                        </span>
                        <span className="text-red-600 font-black tracking-[0.2em] text-xs leading-none">
                            PRO
                        </span>
                    </div>
                </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto scrollbar-hide">
                {navItems.filter(item => !item.hidden).map((item) => {
                    const Icon = item.icon;
                    const isActive = activeTab === item.id;

                    return (
                        <button
                            key={item.id}
                            id={item.elementId}
                            onClick={() => !item.disabled && onNavigate(item.id)}
                            disabled={item.disabled}
                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group relative overflow-hidden
                ${isActive
                                    ? 'bg-red-600 text-white shadow-[0_0_20px_rgba(220,38,38,0.3)]'
                                    : 'text-neutral-400 hover:bg-neutral-900 hover:text-white'
                                }
                ${item.disabled ? 'opacity-40 cursor-not-allowed hover:bg-transparent hover:text-neutral-400' : ''}
              `}
                        >
                            <Icon
                                size={20}
                                strokeWidth={isActive ? 2.5 : 2}
                                className={`transition-transform duration-200 ${isActive ? 'scale-110' : 'group-hover:scale-110'}`}
                            />
                            <span className={`font-bold text-sm uppercase tracking-wider flex-1 ${isActive ? 'text-white' : ''}`}>
                                {item.label}
                            </span>

                            {/* Notification Badge */}
                            {((role === 'trainer' && item.id === 'home') || (role === 'athlete' && item.id === 'coach')) && pendingRequests > 0 && (
                                <div className="bg-yellow-500 text-black text-[10px] font-black px-1.5 py-0.5 rounded-full shadow-lg shadow-yellow-500/20 animate-pulse">
                                    {pendingRequests}
                                </div>
                            )}

                            {/* Friend Requests Badge */}
                            {item.id === 'friends' && friendRequestsCount > 0 && (
                                <div className="bg-red-500 text-white text-[10px] font-black px-1.5 py-0.5 rounded-full shadow-lg shadow-red-500/20 animate-pulse">
                                    {friendRequestsCount}
                                </div>
                            )}

                            {/* Active Indicator Line */}
                            {isActive && (
                                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white/20 rounded-r-full" />
                            )}
                        </button>
                    );
                })}
            </nav>

            {/* Footer / User Profile */}
            <div className="p-4 border-t border-neutral-900 bg-black/40 backdrop-blur-sm">
                <div className="flex items-center gap-2 mb-4 px-1">
                    <LanguageSwitcher direction="up" align="left" />
                    {/* Tutorial Toggle */}
                    <button
                        onClick={onShowTutorial}
                        className="p-2.5 rounded-lg bg-neutral-900 hover:bg-neutral-800 text-neutral-400 hover:text-white transition-all border border-transparent hover:border-neutral-800"
                        title={t('common.tutorial')}
                    >
                        <HelpCircle size={18} />
                    </button>
                </div>

                <div className="flex items-center gap-3 p-3.5 rounded-2xl border transition-all duration-300 relative overflow-hidden group shadow-lg bg-neutral-900/40 backdrop-blur-md border-neutral-800 hover:border-neutral-700 shadow-black/40">
                    {/* Visual accent */}
                    {activeTab === 'profile' && (
                        <div className="absolute top-0 left-0 w-1 h-full bg-red-600 opacity-50" />
                    )}

                    <div className="w-11 h-11 rounded-full border-2 transition-all duration-300 flex items-center justify-center overflow-hidden flex-shrink-0 relative z-10 shadow-sm bg-neutral-800 border-neutral-700/50 group-hover:border-red-600/50">
                        {profile?.profilePicture ? (
                            <img src={profile.profilePicture} alt="Profile" loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                        ) : (
                            <span className="font-black text-base text-white">
                                {profile?.displayName?.charAt(0).toUpperCase() || user?.displayName?.charAt(0).toUpperCase() || 'U'}
                            </span>
                        )}
                    </div>

                    <div className="flex-1 min-w-0 z-10">
                        <div className="flex flex-col gap-1">
                            <p
                                className="text-sm font-black break-words leading-none tracking-tight transition-colors text-white group-hover:text-red-500"
                                title={profile?.username ? `@${profile.username}` : user?.displayName}
                            >
                                {profile?.username ? `@${profile.username}` : (user?.displayName || t('profile.default_user'))}
                            </p>

                            <div className="flex">
                                <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full tracking-widest flex-shrink-0 flex items-center gap-1 ${role === 'trainer'
                                    ? 'bg-blue-500/10 text-blue-500 border border-blue-500/20'
                                    : 'bg-red-500/10 text-red-500 border border-red-500/10'
                                    }`}>
                                    <div className={`w-1 h-1 rounded-full ${role === 'trainer' ? 'bg-blue-500' : 'bg-red-500'} animate-pulse`} />
                                    {role === 'trainer' ? t('profile.trainer_role') : t('profile.athlete_role')}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center z-10">
                        <button
                            onClick={onLogout}
                            className="p-2 rounded-xl transition-all duration-200 text-neutral-500 hover:text-white hover:bg-neutral-800"
                            title={t('auth.logout')}
                        >
                            <LogOut size={16} />
                        </button>
                    </div>
                </div>
            </div>
        </aside>
    );
};

export default Sidebar;
